/*
 * Copyright (C) 2019-2022 Corvalent Corporation - All Rights Reserved
 * This software is licensed program property and is Proprietary and Confidential.
 * Unauthorized copying of this file via any medium is strictly prohibited unless
 * prior written permission has been obtained by Corvalent Corporation.
 * In addition, the look and feel of this software as well as the user interface
 * design is also Copyright ©.
 * Notice: This software is not open source.
 * This software is subject to the Corvalent Software License Agreement.
 * You should have received a copy of this license with the software.
 * If not, please write to: sales@corvalent.com
 * All information contained herein is, and remains, the property of
 * Corvalent Corporation and its suppliers, if any. This software may also
 * be covered by U.S. and Foreign Patents, patents in progress, and are
 * protected by trade secret or copyright law
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';

import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import { ErrorOutline } from '@mui/icons-material';
import ModalWithActions from '../../ModalWithActions';
import { useGeneralContext } from '../../../context/GeneralContext';
import AddBatchDeviceForm from './AddBatchDeviceForm';
import { addBatchDevices } from '../../../services/zigbeeService';
import { validateTimeout } from '../../../utils/validations/validateAssetWizard';
import { useGetAll } from '../../../hooks/useGetAll';
import Spinner from '../../shared/atoms/Spinner';
import { useLocationsCacheContext } from '../../../context/LocationsCacheContext';
import Yup from '../../../services/yupLocale';

const initialBatchDeviceObj = {
  locationName: '',
  timeout: '',
  user_group: [],
};

const BatchDeviceSchema = Yup.object().shape({
  locationName: Yup.string().required(),
  timeout: Yup.string().required(),
  user_group: Yup.array().of(Yup.string()).min(1).required(),
});

const AddBatchDeviceModal = ({
  onSenderModalHide,
  modalShow,
  gateway,
  deviceIds,
  locationName,
}) => {
  const { openToast } = useGeneralContext();
  const { buildings } = useLocationsCacheContext();

  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState([]); // { status: '', id: '' }
  const formRef = useRef();
  const history = useHistory();
  const { t, i18n } = useTranslation();

  const reset = () => {
    setIsLoading(false);
    setIsValid(false);
    setSuccess(false);
    setErrors([]);
    setSelectedLocationId(null);
  };

  const errorMessages = useMemo(() => {
    const errorsWithTranslation = errors?.filter(item =>
      i18n.exists(`modal.add_batch_device.validation.${item?.status}`),
    );

    const genericErrors =
      errorsWithTranslation
        .filter(item => item.status !== 'no_zone_found_for_device')
        .map(item =>
          t(
            `modal.add_batch_device.validation.${item.status}`,
            item.id
              ? {
                  id: item.id,
                }
              : null,
          ),
        ) ?? [];

    const missingZoneErrorMap = errorsWithTranslation
      .filter(item => item.status === 'no_zone_found_for_device')
      .reduce((acc, curr) => {
        const currId = curr.id.split('|')[0];
        if (!acc[currId]) acc = { ...acc, [currId]: [] };

        acc[currId] = [...acc[currId], curr.room];

        return acc;
      }, {});

    const missingZoneMessages =
      Object.entries(missingZoneErrorMap).map(([key, value]) =>
        t(
          `modal.add_batch_device.validation.no_zone_found_for_device`,
          key
            ? {
                prefix: key,
                rooms: value.join(', '),
                rooms_count: value.length,
              }
            : null,
        ),
      ) ?? [];

    return genericErrors.concat(missingZoneMessages);
  }, [t, i18n, errors]);

  const hasError = useCallback(
    status => !!errors.find(e => e.status === status),
    [errors],
  );

  const {
    data: { location: locations },
    isFetching: isFetchingLocations,
  } = useGetAll('location');

  const submitAddBatchDevice = async values => {
    setIsLoading(true);
    setSelectedLocationId(
      locations.find(l => l.label === values.locationName)?.value,
    );

    try {
      await addBatchDevices(gateway, {
        deviceIds,
        locationName: values.locationName,
        timeout: values.timeout,
        user_group: values.user_group,
      });
      openToast(t('modal.add_batch_device.accepted'));
      setSuccess(true);
      hideAndGoToSensors();
    } catch (e) {
      console.error(e);
      setSuccess(false);

      if (e?.response?.data?.message || e?.response?.data?.items?.length > 0) {
        setErrors([
          e?.response?.data?.message
            ? { status: e?.response?.data?.message }
            : undefined,
          ...(e?.response?.data?.items ?? []),
        ]);
      } else {
        openToast(t('modal.add_batch_device.error'), 'error');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleValidate = values => {
    const errors = {};
    const timeoutError = validateTimeout(values.timeout);

    if (values.timeout.length && timeoutError) errors.timeout = timeoutError;

    return errors;
  };

  const hideAndGoToSensors = () => {
    onSenderModalHide({ reset: true });
    history.push('/portal/management/sensors');
  };

  const goToAddZone = () => {
    onSenderModalHide({ reset: true });

    const firstLocationBuilding = buildings.find(
      b => b.properties.parent === selectedLocationId,
    );

    if (firstLocationBuilding)
      history.push(
        `/portal/maps/facilities/${firstLocationBuilding.properties.id}`,
      );
  };

  if (!success && errorMessages.length)
    return (
      <ModalWithActions
        preventClickOutside
        show={modalShow}
        modalTitle={t('modal.add_batch_device.fail')}
        onHide={onSenderModalHide}
        cancelActionVisible={false}
        options={[
          ...(hasError('no_zone_found_for_device')
            ? [
                {
                  label: t('modal.add_batch_device.select_another_location'),
                  onClick: reset,
                  stay: true,
                },
                {
                  label: t('modal.add_batch_device.add_zones'),
                  onClick: goToAddZone,
                },
              ]
            : []),
          {
            label: t('modal.close'),
            onClick: hideAndGoToSensors,
            secondary: true,
          },
        ]}
        inputComponent={
          <List
            sx={{
              maxHeight: '200px',
              overflowY: 'auto',
            }}
          >
            {errorMessages.map(message => (
              <ListItem key={message} disablePadding>
                <ListItemButton sx={{ cursor: 'default !important' }}>
                  <ListItemIcon>
                    <ErrorOutline color="error" />
                  </ListItemIcon>
                  <ListItemText primary={message} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        }
      />
    );

  return (
    <ModalWithActions
      preventClickOutside
      show={modalShow}
      modalTitle={
        !isLoading
          ? t('modal.add_batch_device.title')
          : t('modal.add_batch_device.loading')
      }
      onHide={onSenderModalHide}
      action={formRef.current?.handleSubmit}
      btCancelDisabled={isLoading}
      btActionDisabled={!isValid || isLoading}
      inputComponent={
        isLoading ? (
          <Box
            width="100%"
            height="100%"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <Spinner />
          </Box>
        ) : (
          <Formik
            initialValues={{
              ...initialBatchDeviceObj,
              locationName: locations.find(l => l.label === locationName)
                ? locationName
                : '',
            }}
            validateOnBlur
            validateOnChange
            validationSchema={BatchDeviceSchema}
            validate={handleValidate}
            enableReinitialize={false}
            onSubmit={submitAddBatchDevice}
            innerRef={formRef}
          >
            {props => (
              <AddBatchDeviceForm
                {...props}
                locations={locations}
                isFetchingLocations={isFetchingLocations}
                setIsValid={setIsValid}
              />
            )}
          </Formik>
        )
      }
    />
  );
};

AddBatchDeviceModal.propTypes = {
  onSenderModalHide: PropTypes.func.isRequired,
  modalShow: PropTypes.bool.isRequired,
  gateway: PropTypes.string.isRequired,
  locationName: PropTypes.string,
  deviceIds: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default AddBatchDeviceModal;
